import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';
import { Users, RefreshCw, MessageSquarePlus, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/apiBase';

const STATUS_META = {
  new:            { label: 'New',            cls: 'bg-[#dbeafe] text-[#1e40af]' },
  contacted:      { label: 'Contacted',      cls: 'bg-[#fef3c7] text-[#92400e]' },
  replied:        { label: 'Replied',        cls: 'bg-[#ede9fe] text-[#5b21b6]' },
  converted:      { label: 'Converted',      cls: 'bg-[#d1fae5] text-[#065f46]' },
  not_interested: { label: 'Not interested', cls: 'bg-[#f4f5f7] text-[#6b7280]' },
};
const STATUS_KEYS = Object.keys(STATUS_META);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const scoreColor = (s) => (s >= 75 ? '#10b981' : s >= 50 ? '#eab308' : '#e11d48');

const META_SOURCES = ['facebook', 'fb', 'meta', 'instagram', 'ig', 'paid_social'];
const isMetaLead = (lead) =>
  lead.source === 'meta_lead_ad' || META_SOURCES.includes((lead.utm_source || '').toLowerCase());

function LeadRow({ lead, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const update = useCallback(async (payload) => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/leads/${encodeURIComponent(lead.email)}/follow-up`,
        payload,
        { withCredentials: true },
      );
      onUpdated(lead.email, res.data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [lead.email, onUpdated]);

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    if (await update({ status })) toast.success(`Marked ${STATUS_META[status]?.label || status}`);
  };

  const handleAddNote = async () => {
    const text = note.trim();
    if (!text) return;
    if (await update({ note: text })) {
      setNote('');
      toast.success('Note added');
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(lead.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  const status = lead.follow_up_status || 'new';
  const notes = lead.follow_up_notes || [];

  return (
    <div className="bg-white border border-[#e5e7eb]" data-testid={`lead-row-${lead.email}`}>
      <div className="p-4 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-[#0f1115] truncate" data-testid={`lead-email-${lead.email}`}>{lead.email}</span>
            <button onClick={copyEmail} className="text-[#9ca3af] hover:text-[#002fa7]" title="Copy email" data-testid={`lead-copy-${lead.email}`}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
            {lead.converted_to_user && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#d1fae5] text-[#065f46] px-1.5 py-0.5">Signed up</span>
            )}
            {isMetaLead(lead) && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider bg-[#1877f2] text-white px-1.5 py-0.5"
                title={lead.utm_campaign ? `Campaign: ${lead.utm_campaign}` : 'Meta ad traffic'}
                data-testid={`lead-meta-badge-${lead.email}`}
              >
                Meta
              </span>
            )}
            {!isMetaLead(lead) && lead.utm_source && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider bg-[#f4f5f7] text-[#4b5563] px-1.5 py-0.5"
                title={lead.utm_campaign ? `Campaign: ${lead.utm_campaign}` : ''}
                data-testid={`lead-utm-badge-${lead.email}`}
              >
                {lead.utm_source}
              </span>
            )}
          </div>
          <p className="text-xs text-[#6b7280] mt-0.5">
            {lead.name && `${lead.name} · `}
            {lead.target_job || 'No target job'} · {lead.source === 'meta_lead_ad' ? 'Meta Lead Ad' : (lead.source || 'free_ats_checker')}
            {lead.utm_campaign && ` · ${lead.utm_campaign}`}
            {lead.phone && ` · ${lead.phone}`}
            {` · captured ${fmtDate(lead.created_at)}`}
            {lead.checks_count > 1 && ` · ${lead.checks_count} checks`}
          </p>
        </div>

        {lead.last_score != null && (
          <span
            className="text-sm font-black px-2 py-1 text-white flex-shrink-0"
            style={{ background: scoreColor(lead.last_score) }}
            title="Latest ATS score"
            data-testid={`lead-score-${lead.email}`}
          >
            {lead.last_score}
          </span>
        )}

        <select
          value={status}
          onChange={handleStatusChange}
          disabled={saving}
          className={`text-xs font-semibold px-2 py-1.5 border-0 cursor-pointer ${STATUS_META[status]?.cls || ''}`}
          data-testid={`lead-status-select-${lead.email}`}
        >
          {STATUS_KEYS.map((k) => (
            <option key={k} value={k}>{STATUS_META[k].label}</option>
          ))}
        </select>

        <button
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#4b5563] hover:text-[#002fa7]"
          data-testid={`lead-notes-toggle-${lead.email}`}
        >
          <MessageSquarePlus size={14} />
          Notes{notes.length > 0 && ` (${notes.length})`}
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[#e5e7eb] bg-[#f9fafb] p-4 space-y-3" data-testid={`lead-notes-panel-${lead.email}`}>
          {notes.length === 0 ? (
            <p className="text-xs text-[#9ca3af]">No notes yet.</p>
          ) : (
            <ul className="space-y-2">
              {[...notes].reverse().map((n, i) => (
                <li key={i} className="text-xs text-[#0f1115] bg-white border border-[#e5e7eb] p-2.5">
                  <p>{n.note}</p>
                  <p className="text-[10px] text-[#9ca3af] mt-1">{n.by} · {fmtDate(n.at)}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }}
              placeholder="Add a follow-up note (e.g., 'Emailed intro on Tue, waiting on reply')"
              className="flex-1 px-3 py-2 text-xs border border-[#e5e7eb] focus:outline-none focus:border-[#002fa7]"
              data-testid={`lead-note-input-${lead.email}`}
            />
            <button
              onClick={handleAddNote}
              disabled={saving || !note.trim()}
              className="px-3 py-2 bg-[#002fa7] text-white text-xs font-bold hover:bg-[#001d6a] disabled:opacity-50"
              data-testid={`lead-note-save-${lead.email}`}
            >
              {saving ? 'Saving…' : 'Add note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [metaStatus, setMetaStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchMetaStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/meta-leads/status`, { withCredentials: true });
      setMetaStatus(res.data);
    } catch { /* soft-fail */ }
  }, []);

  const triggerMetaSync = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/meta-leads/sync`, {}, { withCredentials: true });
      if (res.data.ok) {
        toast.success(`Meta sync done — ${res.data.imported} new lead(s) imported`);
      } else {
        toast.error(res.data.error || 'Meta sync failed');
      }
      fetchMetaStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Meta sync failed');
    } finally {
      setSyncing(false);
    }
  }, [fetchMetaStatus]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/recent`, {
        params: { limit: 500 },
        withCredentials: true,
      });
      setLeads(res.data.leads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchLeads();
      fetchMetaStatus();
    }
  }, [user, fetchLeads, fetchMetaStatus]);

  const triggerRefreshAll = useCallback(() => {
    fetchLeads();
    fetchMetaStatus();
  }, [fetchLeads, fetchMetaStatus]);

  const onLeadUpdated = useCallback((email, data) => {
    setLeads((prev) => prev.map((l) => (l.email === email ? {
      ...l,
      follow_up_status: data.follow_up_status,
      follow_up_notes: data.follow_up_notes,
      follow_up_updated_at: data.follow_up_updated_at,
    } : l)));
  }, []);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const counts = leads.reduce((acc, l) => {
    const s = l.follow_up_status || 'new';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const q = search.trim().toLowerCase();
  const visible = leads.filter((l) => {
    if (statusFilter !== 'all' && (l.follow_up_status || 'new') !== statusFilter) return false;
    if (q && !`${l.email} ${l.target_job || ''}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-leads-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Users size={20} className="text-[#002fa7]" />
            <h1 className="font-satoshi text-xl font-bold text-[#0f1115]">Leads — Follow-up Pipeline</h1>
          </div>
          <button
            onClick={triggerRefreshAll}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4b5563] border border-[#e5e7eb] bg-white px-3 py-1.5 hover:text-[#002fa7] disabled:opacity-50"
            data-testid="leads-refresh-btn"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 text-xs bg-white border border-[#e5e7eb] px-3 py-2" data-testid="meta-sync-bar">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: metaStatus?.configured ? '#10b981' : '#9ca3af' }} />
          {metaStatus?.configured ? (
            <>
              <span className="text-[#4b5563]">
                <strong>Meta Lead Ads:</strong> connected · {metaStatus.imported_total} imported
                {metaStatus.last_sync_at && ` · last sync ${fmtDate(metaStatus.last_sync_at)}`}
                {` · auto-syncs every ${metaStatus.interval_minutes} min`}
              </span>
              <button
                onClick={triggerMetaSync}
                disabled={syncing}
                className="ml-auto font-bold text-[#002fa7] hover:underline disabled:opacity-50"
                data-testid="meta-sync-now-btn"
              >
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>
            </>
          ) : (
            <span className="text-[#6b7280]">
              <strong>Meta Lead Ads:</strong> not connected — instant-form leads stay in Meta Leads Center until a Page access token is added.
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`text-xs font-semibold px-2.5 py-1.5 border ${statusFilter === 'all' ? 'bg-[#0f1115] text-white border-[#0f1115]' : 'bg-white border-[#e5e7eb] text-[#4b5563]'}`}
            data-testid="leads-filter-all"
          >
            All ({leads.length})
          </button>
          {STATUS_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`text-xs font-semibold px-2.5 py-1.5 border ${statusFilter === k ? 'bg-[#0f1115] text-white border-[#0f1115]' : `border-transparent ${STATUS_META[k].cls}`}`}
              data-testid={`leads-filter-${k}`}
            >
              {STATUS_META[k].label} ({counts[k] || 0})
            </button>
          ))}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or job…"
            className="ml-auto px-3 py-1.5 text-xs border border-[#e5e7eb] focus:outline-none focus:border-[#002fa7] w-48"
            data-testid="leads-search-input"
          />
        </div>

        {loading ? (
          <p className="text-center text-sm text-[#6b7280] py-12">Loading leads…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-sm text-[#6b7280] py-12" data-testid="leads-empty-state">
            {leads.length === 0 ? 'No leads captured yet.' : 'No leads match this filter.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="leads-list">
            {visible.map((l) => (
              <LeadRow key={l.email} lead={l} onUpdated={onLeadUpdated} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
